import React, { useEffect, useId, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlayCircle, Users, Video } from 'lucide-react';
import { AppButton, AppCard, AppCardContent, StatusBadge } from '../../../ui';
import { usePlan } from '../../../../contexts/PlanContext';
import apiClient from '../../../../utils/apiClient';
import { resolveSessionKind } from '../../../DemoClasses/sessionKind';
import CardHead from './CardHead';

/** How many sessions the card lists; the module's own page has the rest. */
const SHOWN = 4;

const when = (dt) => {
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
};

/** Live first, then the soonest scheduled. Ended and cancelled sessions are history, not a dashboard item. */
const upcoming = (sessions) => sessions
  .filter((s) => s.status === 'live' || s.status === 'scheduled')
  .sort((a, b) => {
    if ((a.status === 'live') !== (b.status === 'live')) return a.status === 'live' ? -1 : 1;
    return new Date(a.scheduledStartAt) - new Date(b.scheduledStartAt);
  });

/**
 * DemoSessionsCard — the trainer's live and upcoming public sessions: demo
 * classes OR webinars, by `kind`. Mounted once per product, and each mount is
 * plan-gated and renders nothing when there is nothing coming up, so most
 * trainers see neither.
 *
 * Props:
 *   kind    — "demo" (default) | "webinar"
 *   onToast — (message, type) => void; told when a start fails
 */
const DemoSessionsCard = ({ kind = 'demo', onToast }) => {
  const navigate = useNavigate();
  const titleId = useId();
  const { hasPlanFeature } = usePlan();
  const vocab = resolveSessionKind(kind);
  const enabled = hasPlanFeature(vocab.planFeature);
  const { apiBase } = vocab;

  const [sessions, setSessions] = useState([]);
  const [starting, setStarting] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    apiClient.get(`${apiBase}/sessions`, { params: { pageSize: 20 } })
      .then(({ data }) => { if (alive) setSessions(upcoming(data?.items || [])); })
      .catch(() => { if (alive) setSessions([]); });
    return () => { alive = false; };
  }, [enabled, apiBase]);

  if (!enabled || sessions.length === 0) return null;

  const start = async (session) => {
    setStarting(session._id);
    try {
      await apiClient.post(`${apiBase}/sessions/${session._id}/start`);
      navigate(`${vocab.instructorBase}/${session._id}/host`);
    } catch (err) {
      onToast?.(err?.response?.data?.message || `Could not start the ${vocab.noun}.`, 'danger');
      setStarting(null);
    }
  };

  return (
    <AppCard as="section" aria-labelledby={titleId}>
      <CardHead
        id={titleId}
        title={vocab.moduleTitle}
        description={`${sessions.length} live or upcoming`}
        linkTo={vocab.instructorBase}
        linkLabel="View all"
      />
      <AppCardContent>
        <ul className="tw-m-0 tw-list-none tw-p-0">
          {sessions.slice(0, SHOWN).map((s) => (
            <li
              key={s._id}
              className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-2 tw-border-0 tw-border-t tw-border-solid tw-border-line-soft tw-py-3 first:tw-border-t-0 first:tw-pt-0 last:tw-pb-0"
            >
              <div className="tw-min-w-0 tw-flex-[1_1_14rem]">
                <Link
                  to={`${vocab.instructorBase}/${s._id}`}
                  className="tw-block tw-truncate tw-rounded-sm tw-text-sm tw-font-semibold tw-text-content tw-no-underline tw-focus-ring hover:tw-text-primary"
                >
                  {s.title}
                </Link>
                <p className="tw-m-0 tw-mt-0.5 tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-content-muted">
                  <span>{when(s.scheduledStartAt)}</span>
                  <span aria-hidden="true">·</span>
                  <Users size={12} aria-hidden="true" />
                  <span>{s.registrantCount || 0} registered</span>
                </p>
              </div>
              <StatusBadge status={s.status} domain="demoSession" />
              <div className="tw-ml-auto tw-flex tw-shrink-0 tw-items-center tw-gap-2">
                {s.status === 'live' ? (
                  <AppButton as={Link} to={`${vocab.instructorBase}/${s._id}/host`} size="sm">
                    <Video size={16} aria-hidden="true" /> Host
                  </AppButton>
                ) : (
                  <AppButton size="sm" variant="outline" disabled={starting === s._id} onClick={() => start(s)}>
                    <PlayCircle size={16} aria-hidden="true" /> {starting === s._id ? 'Starting…' : 'Start'}
                  </AppButton>
                )}
              </div>
            </li>
          ))}
        </ul>
      </AppCardContent>
    </AppCard>
  );
};

export default DemoSessionsCard;
